import * as React from "react";
import { cn } from "@/lib/utils";

interface RadioGroupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  value?: string;
  onValueChange?: (value: string) => void;
}

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ className, value, onValueChange, children, ...props }, ref) => {
    const contextValue = React.useMemo(
      () => ({
        value,
        onValueChange,
      }),
      [value, onValueChange]
    );

    return (
      <RadioGroupContext.Provider value={contextValue}>
        <div ref={ref} className={cn("grid gap-2", className)} role="radiogroup" {...props}>
          {children}
        </div>
      </RadioGroupContext.Provider>
    );
  }
);
RadioGroup.displayName = "RadioGroup";

interface RadioGroupContextValue {
  value?: string;
  onValueChange?: (value: string) => void;
}

const RadioGroupContext = React.createContext<RadioGroupContextValue>({});

interface RadioGroupItemProps {
  value: string;
  label: string;
  description?: string | React.ReactNode;
  disabled?: boolean;
}

function RadioGroupItem({ value, label, description, disabled = false }: RadioGroupItemProps) {
  const context = React.useContext(RadioGroupContext);
  const inputId = `radio-${value}`;
  const checked = context.value === value;

  const handleChange = () => {
    if (disabled) return;
    if (!context.onValueChange) {
      console.warn(
        "RadioGroupItem must be used inside a RadioGroup component. Wrap your RadioGroupItems with <RadioGroup>."
      );
      return;
    }
    context.onValueChange(value);
  };

  return (
    <label
      htmlFor={inputId}
      className={cn(
        "flex items-start space-x-3 rounded-md border border-input bg-background p-4 transition-colors",
        disabled
          ? "cursor-not-allowed opacity-60"
          : "cursor-pointer hover:bg-accent hover:text-accent-foreground"
      )}
    >
      <input
        type="radio"
        id={inputId}
        value={value}
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        className="sr-only"
      />
      <div className="relative mt-0.5 flex-shrink-0">
        <div
          className={cn(
            "aspect-square h-4 w-4 rounded-full border-2 ring-offset-background transition-all",
            checked ? "border-primary bg-primary" : "border-primary/50 bg-background"
          )}
        >
          {checked && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-2 w-2 rounded-full bg-primary-foreground" />
            </div>
          )}
        </div>
      </div>
      <div className="flex-1">
        <div className="font-medium">{label}</div>
        {description && <div className="text-sm text-muted-foreground mt-1">{description}</div>}
      </div>
    </label>
  );
}

export { RadioGroup, RadioGroupItem };
